import { useState, useCallback } from "react";
import firebase from "firebase/app";
import "firebase/firestore";
import { Habit } from "../../types/Habit";
import { Action, Subaction } from "../../types/ActionType";
import DB_PATH from "../DB_PATH";

export const useHabitOperations = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sanitizeData = (value: any): any => {
    if (Array.isArray(value)) {
      return value.map((item) => {
        const sanitized = sanitizeData(item);
        return sanitized === undefined ? null : sanitized;
      });
    }
    if (value && typeof value === "object") {
      return Object.entries(value).reduce((acc, [key, val]) => {
        const sanitized = sanitizeData(val);
        if (sanitized !== undefined) {
          acc[key] = sanitized;
        }
        return acc;
      }, {} as any);
    }
    return value === undefined ? undefined : value;
  };

  const loadPersistedActionsForHabit = useCallback(async (habitId: string) => {
    const [actionsSnapshot, subactionsSnapshot] = await Promise.all([
      firebase
        .firestore()
        .collection(DB_PATH.HABIT_ACTION)
        .where("habitId", "==", habitId)
        .get(),
      firebase
        .firestore()
        .collection(DB_PATH.HABIT_SUBACTION)
        .where("habitId", "==", habitId)
        .get(),
    ]);

    const allSubactions = subactionsSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() } as Subaction)
    );

    return actionsSnapshot.docs.map((doc) => {
      const action = { id: doc.id, ...doc.data() } as Action;
      action.subactions = allSubactions.filter(
        (subaction) => subaction.actionId === action.id
      );
      return action;
    });
  }, []);

  const loadStandaloneSubactionsForHabit = useCallback(async (habitId: string) => {
    const snapshot = await firebase
      .firestore()
      .collection(DB_PATH.HABIT_SUBACTION)
      .where("habitId", "==", habitId)
      .get();

    return snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() } as Subaction))
      .filter((subaction) => !subaction.actionId);
  }, []);

  const syncActionsForHabit = useCallback(async (habit: Habit) => {
    if (!habit?.id) return;

    const actions = Array.isArray(habit.actions) ? habit.actions : [];
    const actionCollection = firebase
      .firestore()
      .collection(DB_PATH.HABIT_ACTION);

    const actionIds = new Set(
      actions.filter((action) => !!action?.id).map((action) => action.id)
    );

    const upsertPromises = actions
      .filter((action) => !!action?.id)
      .map((action) => {
        const { subactions, ...actionPayload } = action as any;
        const payload: any = {
          ...sanitizeData(actionPayload),
          habitId: habit.id,
          communityId: habit.communityId,
        };

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined) delete payload[key];
        });

        return actionCollection.doc(action.id).set(payload, { merge: true });
      });

    const existingSnapshot = await actionCollection
      .where("habitId", "==", habit.id)
      .get();

    const deletePromises = existingSnapshot.docs
      .filter((doc) => !actionIds.has(doc.id))
      .map((doc) => doc.ref.delete());

    await Promise.all([...upsertPromises, ...deletePromises]);
  }, []);

  const syncSubactionsForHabit = useCallback(async (habit: Habit) => {
    if (!habit?.id) return;

    const actions = Array.isArray(habit.actions) ? habit.actions : [];
    const standalone = Array.isArray(habit.standaloneSubactions)
      ? habit.standaloneSubactions
      : [];

    const actionSubactions = actions.flatMap((action) =>
      (action.subactions || [])
        .filter((subaction) => !!subaction?.id)
        .map((subaction) => ({
          subaction,
          actionId: action.id,
        }))
    );

    const standaloneSubactions = standalone
      .filter((subaction) => !!subaction?.id)
      .map((subaction) => ({
        subaction,
        actionId: undefined,
      }));

    const subactionEntries = [...actionSubactions, ...standaloneSubactions];

    const subactionIds = new Set(
      subactionEntries.map(({ subaction }) => subaction.id)
    );

    const subactionCollection = firebase
      .firestore()
      .collection(DB_PATH.HABIT_SUBACTION);

    const upsertPromises = subactionEntries.map(({ subaction, actionId }) => {
      const payload: any = {
        ...subaction,
        habitId: habit.id,
        communityId: habit.communityId,
      };
      if (actionId) payload.actionId = actionId;
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) delete payload[key];
      });
      return subactionCollection.doc(subaction.id).set(payload, { merge: true });
    });

    const existingSnapshot = await subactionCollection
      .where("habitId", "==", habit.id)
      .get();

    const deletePromises = existingSnapshot.docs
      .filter((doc) => !subactionIds.has(doc.id))
      .map((doc) => doc.ref.delete());

    await Promise.all([...upsertPromises, ...deletePromises]);
  }, []);

  const createHabit = useCallback(async (newHabit: Omit<Habit, "id">) => {
    setIsLoading(true);
    setError(null);

    try {
      const habitsCollection = firebase.firestore().collection(DB_PATH.HABIT);

      // Generate a new ID
      const newHabitRef = habitsCollection.doc();
      const newId = newHabitRef.id;

      // Create the full habit object with the new ID
      const fullHabit: Habit = { ...newHabit, id: newId };

      // Save the habit with the predefined ID. Actions and subactions live in
      // their own collections and are synced separately.
      const { actions, standaloneSubactions, ...habitPayload } = fullHabit as any;
      await newHabitRef.set(sanitizeData(habitPayload));

      await syncActionsForHabit(fullHabit);
      await syncSubactionsForHabit(fullHabit);

      setIsLoading(false);
      return { success: true, habit: fullHabit };
    } catch (err) {
      setError(err.message || "An error occurred while creating the habit");
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, [syncActionsForHabit, syncSubactionsForHabit]);

  const editHabit = useCallback(
    async (habitId: string, updatedHabit: Partial<Habit>) => {
      setIsLoading(true);
      setError(null);

      try {
        const { actions, standaloneSubactions, ...habitPayload } =
          updatedHabit as any;
        const sanitizedPayload = sanitizeData(habitPayload);
        if (actions !== undefined) {
          sanitizedPayload.actions = firebase.firestore.FieldValue.delete();
        }
        if (standaloneSubactions !== undefined) {
          sanitizedPayload.standaloneSubactions =
            firebase.firestore.FieldValue.delete();
        }

        await firebase
          .firestore()
          .collection(DB_PATH.HABIT)
          .doc(habitId)
          .update(sanitizedPayload);

        const shouldSyncActions = Array.isArray(updatedHabit.actions);
        const shouldSyncSubactions =
          Array.isArray(updatedHabit.actions) ||
          Array.isArray(updatedHabit.standaloneSubactions);

        if (shouldSyncActions || shouldSyncSubactions) {
          let actions = updatedHabit.actions;
          let standaloneSubactions = updatedHabit.standaloneSubactions;
          let communityId = updatedHabit.communityId;

          if (
            !Array.isArray(actions) ||
            !Array.isArray(standaloneSubactions) ||
            !communityId
          ) {
            const [resolvedActions, resolvedStandaloneSubactions, habitSnapshot] =
              await Promise.all([
                Array.isArray(actions)
                  ? Promise.resolve(actions)
                  : loadPersistedActionsForHabit(habitId),
                Array.isArray(standaloneSubactions)
                  ? Promise.resolve(standaloneSubactions)
                  : loadStandaloneSubactionsForHabit(habitId),
                communityId
                  ? Promise.resolve(null)
                  : firebase
                      .firestore()
                      .collection(DB_PATH.HABIT)
                      .doc(habitId)
                      .get(),
              ]);

            actions = resolvedActions;
            standaloneSubactions = resolvedStandaloneSubactions;

            if (!communityId) {
              const habitData = habitSnapshot?.data() as Habit | undefined;
              communityId = habitData?.communityId || "";
            }
          }

          const syncPayload: Habit = {
            ...(updatedHabit as Habit),
            id: habitId,
            communityId: communityId || "",
            actions: actions || [],
            standaloneSubactions: standaloneSubactions || [],
          };

          if (shouldSyncActions) {
            await syncActionsForHabit(syncPayload);
          }

          if (shouldSyncSubactions) {
            await syncSubactionsForHabit(syncPayload);
          }
        }
        setIsLoading(false);
        return { success: true };
      } catch (err) {
        setError(err.message || "An error occurred while updating the habit");
        setIsLoading(false);
        return { success: false, error: err.message };
      }
    },
    [
      loadPersistedActionsForHabit,
      loadStandaloneSubactionsForHabit,
      syncActionsForHabit,
      syncSubactionsForHabit,
    ]
  );

  const removeHabit = useCallback(async ({ id }: any) => {
    setIsLoading(true);
    setError(null);

    console.log("Removing Habit", id);
    try {
      await firebase.firestore().collection(DB_PATH.HABIT).doc(id).delete();
      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message || "An error occurred while removing the habit");
      setIsLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  return { createHabit, editHabit, removeHabit, isLoading, error };
};
